import { useEffect, useRef, useState } from "preact/hooks";
import { batch, useComputed } from "@preact/signals";
import { LAYERS, LAYER_ORDER, dishLabel, kindLabel, mealLabel } from "../data/layers";
import { highlight, typeaheadScore } from "../data/search";
import type { Feature, FoodPoint, LayerId } from "../types";
import {
  dishes, enabledLayers, featuresByLayer, filtersOpen, focusBBox, kinds, layerCounts, layerLoaded, meals, q, searchTokens, select, theme, toggleLayer, toggleTheme,
} from "../state/store";
import { fmtDateRange, stripDiacritics } from "../utils/format";

/** A facet the query names — "breakfast", "chowder", "chipper" — offered above the place hits. */
interface FacetHit { facet: "meal" | "dish" | "kind"; id: string; label: string; count: number }

function facetHits(tokens: string[], food: Feature[]): FacetHit[] {
  if (!tokens.length || !food.length) return [];
  const mc = new Map<string, number>(), dc = new Map<string, number>(), kc = new Map<string, number>();
  for (const f of food) {
    const p = f.raw as FoodPoint;
    for (const m of p.meal || []) mc.set(m, (mc.get(m) ?? 0) + 1);
    for (const d of p.dishes || []) dc.set(d, (dc.get(d) ?? 0) + 1);
    for (const k of p.kinds || []) kc.set(k, (kc.get(k) ?? 0) + 1);
  }
  const hit = (label: string, id: string) => {
    const words = stripDiacritics(`${label} ${id.replace(/_/g, " ")}`).split(/\s+/).filter(Boolean);
    return tokens.every((t) => words.some((w) => w.startsWith(t)));
  };
  const out: FacetHit[] = [];
  for (const [m, n] of mc) if (hit(mealLabel(m), m)) out.push({ facet: "meal", id: m, label: mealLabel(m), count: n });
  for (const [d, n] of dc) if (hit(dishLabel(d), d)) out.push({ facet: "dish", id: d, label: dishLabel(d), count: n });
  for (const [k, n] of kc) if (hit(kindLabel(k), k)) out.push({ facet: "kind", id: k, label: kindLabel(k), count: n });
  return out.sort((a, b) => b.count - a.count).slice(0, 3);
}

/** Apply a facet: switch the food layer on, set that one filter, clear the query, fit the map. */
function applyFacet(h: FacetHit): void {
  const food = featuresByLayer.value.food;
  const match = food.filter((f) => {
    const p = f.raw as FoodPoint;
    return h.facet === "meal" ? (p.meal || []).includes(h.id) : h.facet === "dish" ? (p.dishes || []).includes(h.id) : (p.kinds || []).includes(h.id);
  });
  batch(() => {
    if (!enabledLayers.value.has("food")) toggleLayer("food", true);
    meals.value = new Set(h.facet === "meal" ? [h.id] : []);
    dishes.value = new Set(h.facet === "dish" ? [h.id] : []);
    kinds.value = { ...kinds.value, food: new Set(h.facet === "kind" ? [h.id] : []) };
    q.value = "";
    select(null);
  });
  if (match.length) {
    let w = 180, s = 90, e = -180, n = -90;
    for (const f of match) { w = Math.min(w, f.lon); e = Math.max(e, f.lon); s = Math.min(s, f.lat); n = Math.max(n, f.lat); }
    focusBBox([w - 0.05, s - 0.03, e + 0.05, n + 0.03]);
  }
}

function LayerChips() {
  const counts = layerCounts.value;
  const loaded = layerLoaded.value;
  return (
    <div class="layer-chips" role="group" aria-label="Map layers">
      {LAYER_ORDER.map((id) => {
        const def = LAYERS[id];
        const on = enabledLayers.value.has(id);
        const c = counts[id];
        const empty = loaded[id] && c.total === 0;
        return (
          <button
            type="button"
            class={`layer-chip ${on ? "is-on" : ""} ${empty ? "is-empty" : ""}`}
            style={{ "--layer": def.color }}
            aria-pressed={on}
            title={empty ? `${def.label} — no data yet` : def.blurb}
            onClick={() => toggleLayer(id)}
          >
            <span class={`layer-dot shape-${def.shape}`} aria-hidden="true" />
            <span class="layer-label">{def.label}</span>
            {loaded[id] && <span class="layer-count">{on && c.filtered !== c.total ? `${c.filtered}/${c.total}` : c.total}</span>}
          </button>
        );
      })}
    </div>
  );
}

function SearchBox() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const tokens = searchTokens.value;

  const results = useComputed(() => {
    const toks = searchTokens.value;
    if (!toks.length) return [] as { f: Feature; score: number }[];
    const out: { f: Feature; score: number }[] = [];
    for (const id of LAYER_ORDER as LayerId[]) {
      // Search every loaded layer so a hit in an off layer can switch it on.
      for (const f of featuresByLayer.value[id]) {
        const s = typeaheadScore(f, toks);
        if (s) out.push({ f, score: s + (enabledLayers.value.has(id) ? 5 : 0) });
      }
    }
    out.sort((a, b) => b.score - a.score || a.f.name.localeCompare(b.f.name));
    return out.slice(0, 9);
  }).value;
  const facets = useComputed(() => facetHits(searchTokens.value, featuresByLayer.value.food)).value;
  type Item = { kind: "facet"; h: FacetHit } | { kind: "feature"; r: { f: Feature; score: number } };
  const items: Item[] = [...facets.map((h) => ({ kind: "facet" as const, h })), ...results.map((r) => ({ kind: "feature" as const, r }))];

  useEffect(() => { setActive(-1); }, [items.length, q.value]);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (!(e.target as HTMLElement).closest(".search-wrap")) setOpen(false); };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const choose = (r: { f: Feature }) => {
    setOpen(false);
    if (!enabledLayers.value.has(r.f.layer)) toggleLayer(r.f.layer, true);
    select(r.f, { focus: true });
  };
  const pick = (it: Item) => { if (it.kind === "facet") { setOpen(false); applyFacet(it.h); } else choose(it.r); };
  const onKey = (e: KeyboardEvent) => {
    if (!open || !items.length) { if (e.key === "Escape") { q.value = ""; } return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((active + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((active - 1 + items.length) % items.length); }
    else if (e.key === "Enter") {
      // Enter with nothing highlighted: a query that *is* a facet name ("breakfast") applies it;
      // otherwise a lone hit is chosen; otherwise the query just stays as a list filter.
      const exact = facets.find((h) => stripDiacritics(h.label) === stripDiacritics(q.value.trim()));
      if (active >= 0) { e.preventDefault(); pick(items[active]); }
      else if (exact) { e.preventDefault(); setOpen(false); applyFacet(exact); }
      else if (items.length === 1) { e.preventDefault(); pick(items[0]); }
    }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div class="search-wrap">
      <input
        ref={inputRef}
        type="search"
        class="search-input"
        placeholder="Search places, dishes, towns — Newgrange, chowder, Dingle…"
        value={q.value}
        autocomplete="off"
        spellcheck={false}
        aria-label="Search"
        onInput={(e) => { q.value = (e.target as HTMLInputElement).value; setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {q.value && (
        <button type="button" class="search-clear" aria-label="Clear search" onClick={() => { q.value = ""; inputRef.current?.focus(); }}>×</button>
      )}
      {open && items.length > 0 && (
        <ul class="typeahead" role="listbox">
          {items.map((it, i) => {
            if (it.kind === "facet") {
              const h = it.h;
              return (
                <li
                  role="option"
                  aria-selected={i === active}
                  class={`typeahead-item typeahead-facet ${i === active ? "active" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                >
                  <span class="layer-dot shape-square" style={{ "--layer": LAYERS.food.color }} aria-hidden="true" />
                  <span class="ta-text">
                    <span class="ta-name">{h.label} <span class="ta-facet-n">{h.count} {h.count === 1 ? "place" : "places"}</span></span>
                    <span class="ta-sub">{LAYERS.food.label}, show them all on the map</span>
                  </span>
                </li>
              );
            }
            const r = it.r;
            const def = LAYERS[r.f.layer];
            const dr = r.f.layer === "history" ? fmtDateRange(r.f.dateStart, r.f.dateEnd) : "";
            const sub = [r.f.sub, dr, def.label].filter(Boolean).join(", ");
            return (
              <li
                role="option"
                aria-selected={i === active}
                class={`typeahead-item ${i === active ? "active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(r); }}
              >
                <span class={`layer-dot shape-${def.shape}`} style={{ "--layer": r.f.color }} aria-hidden="true" />
                <span class="ta-text">
                  <span class="ta-name" dangerouslySetInnerHTML={{ __html: highlight(r.f.name, tokens) }} />
                  {sub && <span class="ta-sub">{sub}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function Header() {
  const dark = theme.value === "dark";
  return (
    <header class="app-header">
      <div class="header-row">
        <a class="brand" href="./" aria-label="Ireland travel map — home">
          <span class="brand-title">Ireland</span>
          <span class="brand-tag">a traveler's map</span>
        </a>
        <div class="header-search"><SearchBox /></div>
        <div class="header-actions">
          <button type="button" class="btn-filters" onClick={() => { filtersOpen.value = !filtersOpen.value; }} aria-pressed={filtersOpen.value}>
            Filters
          </button>
          <button type="button" class="btn-theme" onClick={toggleTheme} aria-label={`Switch to ${dark ? "light" : "dark"} mode`} title="Toggle theme">
            {dark ? "☾" : "☀"}
          </button>
        </div>
      </div>
      <div class="header-row header-row-layers">
        <LayerChips />
      </div>
    </header>
  );
}
